import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ToastContainerComponent } from '@core/components/toast-container/toast-container.component';
import { V2AccountPanelComponent } from './account-panel/v2-account-panel.component';
import { SidebarState } from './menu.model';
import { V2RailComponent } from './rail/v2-rail.component';
import { SidebarStateService } from './sidebar-state.service';
import { V2TabBarComponent } from './tab-bar/v2-tab-bar.component';
import { V2TopBarComponent } from './top-bar/v2-top-bar.component';
import { V2BreadcrumbsComponent } from '../shared/breadcrumbs/v2-breadcrumbs.component';

/**
 * The v2 application shell.
 *
 * Owns the `.evo-v2` scope class -- everything the design layer styles is a
 * descendant of this element -- and the page's ground. The route it is mounted
 * on carries `{ v2: true }`, which stands `CoreComponent`'s header, sidebar and
 * footer down, so this is the only chrome a v2 page renders inside.
 *
 * Navigation is two components rather than one responsive one: the rail above
 * 880px, the bottom tab bar below it. They are different shapes with different
 * contents -- the tab bar carries four destinations, the rail carries every
 * group the viewer is entitled to -- and collapsing a rail into a phone is what
 * the old sidebar does today.
 */
@Component({
  selector: 'app-v2-shell',
  templateUrl: './v2-shell.component.html',
  styleUrls: ['./v2-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    RouterOutlet,
    V2TopBarComponent,
    V2RailComponent,
    V2TabBarComponent,
    V2AccountPanelComponent,
    ToastContainerComponent,
    V2BreadcrumbsComponent,
  ],
})
export class V2ShellComponent {
  private readonly sidebarStateService = inject(SidebarStateService);

  protected readonly accountOpen = signal(false);

  /**
   * The shell is the single writer of the rail's state, as evo-accounts'
   * client layout is: the app bar's toggle and the rail's own section controls
   * both come through here, so there is one description of "is the rail on
   * screen" rather than two to keep in step.
   */
  protected readonly sidebarState = this.sidebarStateService.state;

  protected toggleSidebar(): void {
    this.sidebarStateService.toggleHidden();
  }

  protected onSidebarStateChange(newState: Partial<SidebarState>): void {
    this.sidebarStateService.update(newState);
  }

  protected toggleAccount(): void {
    this.accountOpen.update(open => !open);
  }

  protected closeAccount(): void {
    this.accountOpen.set(false);
  }
}
